/**
 * Redis storage adapter for production use
 */

import type { StorageAdapter, AgentDetectionEvent, AgentSession } from './types';

export interface RedisClient {
  get<T>(key: string): Promise<T | null>;
  set(key: string, value: string | number | Buffer): Promise<string | null>;
  setex(key: string, ttl: number, value: string | number | Buffer): Promise<string | null>;
  zadd(key: string, options: { score: number; member: string }): Promise<number | null>;
  zrange(key: string, start: number, stop: number): Promise<string[]>;
  zrevrange(key: string, start: number, stop: number): Promise<string[]>;
  zcard(key: string): Promise<number | null>;
  zremrangebyrank(key: string, start: number, stop: number): Promise<number | null>;
  del(key: string | string[]): Promise<number>;
  scan(cursor: number, options?: { match?: string; count?: number }): Promise<[string, string[]]>;
}

export class RedisStorageAdapter implements StorageAdapter {
  private redis: RedisClient;
  private ttl: number;
  private keyPrefix: string = 'agent-shield';

  constructor(redis: RedisClient, ttl: number = 86400) {
    this.redis = redis;
    this.ttl = ttl;
  }

  private eventKey(timestamp: string, eventId: string): string {
    return `${this.keyPrefix}:events:${timestamp}:${eventId}`;
  }

  private sessionKey(sessionId: string): string {
    return `${this.keyPrefix}:sessions:${sessionId}`;
  }

  private timelineKey(): string {
    return `${this.keyPrefix}:events:timeline`;
  }

  async storeEvent(event: AgentDetectionEvent): Promise<void> {
    const key = this.eventKey(event.timestamp, event.eventId);

    // Store the event with TTL
    await this.redis.setex(key, this.ttl, JSON.stringify(event));

    // Add to timeline sorted set
    await this.redis.zadd(this.timelineKey(), {
      score: Date.parse(event.timestamp),
      member: key,
    });

    // Maintain timeline size (keep last 1000 events)
    const count = await this.redis.zcard(this.timelineKey());
    if (count && count > 1000) {
      await this.redis.zremrangebyrank(this.timelineKey(), 0, -1001);
    }
  }

  async getRecentEvents(limit: number = 100): Promise<AgentDetectionEvent[]> {
    // Get most recent event keys from timeline
    const keys = await this.redis.zrevrange(this.timelineKey(), 0, limit - 1);

    if (!keys || keys.length === 0) {
      return [];
    }

    // Fetch events
    const events: AgentDetectionEvent[] = [];
    for (const key of keys) {
      const data = await this.redis.get<string>(key);
      if (data) {
        try {
          const event = typeof data === 'string' ? JSON.parse(data) : data;
          events.push(event as AgentDetectionEvent);
        } catch (e) {
          console.error(`Failed to parse event ${key}:`, e);
        }
      }
    }

    return events;
  }

  async getSessionEvents(sessionId: string): Promise<AgentDetectionEvent[]> {
    // Get all event keys from timeline
    const keys = await this.redis.zrevrange(this.timelineKey(), 0, -1);

    if (!keys || keys.length === 0) {
      return [];
    }

    // Fetch and filter events
    const events: AgentDetectionEvent[] = [];
    for (const key of keys) {
      const data = await this.redis.get<string>(key);
      if (data) {
        try {
          const event = typeof data === 'string' ? JSON.parse(data) : data;
          if ((event as AgentDetectionEvent).sessionId === sessionId) {
            events.push(event as AgentDetectionEvent);
          }
        } catch (e) {
          console.error(`Failed to parse event ${key}:`, e);
        }
      }
    }

    return events;
  }

  async storeSession(session: AgentSession): Promise<void> {
    const key = this.sessionKey(session.sessionId);

    // Check if session exists
    const existing = await this.redis.get<string>(key);

    if (existing) {
      // Update existing session
      const existingSession = typeof existing === 'string' ? JSON.parse(existing) : existing;
      const methods = new Set([
        ...(existingSession.verificationMethods || []),
        ...session.verificationMethods,
      ]);

      const updatedSession: AgentSession = {
        ...existingSession,
        lastSeen: session.lastSeen,
        eventCount: session.eventCount,
        paths: Array.from(new Set([...existingSession.paths, ...session.paths])),
        averageConfidence: session.averageConfidence,
        verificationMethods: Array.from(methods),
      };

      await this.redis.setex(key, this.ttl, JSON.stringify(updatedSession));
    } else {
      // Store new session
      await this.redis.setex(key, this.ttl, JSON.stringify(session));
    }
  }

  async getSession(sessionId: string): Promise<AgentSession | null> {
    const key = this.sessionKey(sessionId);
    const data = await this.redis.get<string>(key);

    if (!data) {
      return null;
    }

    try {
      return typeof data === 'string' ? JSON.parse(data) : data;
    } catch (e) {
      console.error(`Failed to parse session ${sessionId}:`, e);
      return null;
    }
  }

  async getRecentSessions(limit: number = 10): Promise<AgentSession[]> {
    // Scan for session keys
    const pattern = `${this.keyPrefix}:sessions:*`;
    const sessions: AgentSession[] = [];
    let cursor = 0;

    do {
      const [nextCursor, keys] = await this.redis.scan(cursor, {
        match: pattern,
        count: 100,
      });

      cursor = parseInt(nextCursor);

      for (const key of keys) {
        const data = await this.redis.get<string>(key);
        if (data) {
          try {
            const session = typeof data === 'string' ? JSON.parse(data) : data;
            sessions.push(session as AgentSession);
          } catch (e) {
            console.error(`Failed to parse session from ${key}:`, e);
          }
        }
      }
    } while (cursor !== 0 && sessions.length < limit * 2); // Get extra to sort

    // Sort by lastSeen and return limit
    return sessions.sort((a, b) => Date.parse(b.lastSeen) - Date.parse(a.lastSeen)).slice(0, limit);
  }

  async cleanup(olderThan: Date): Promise<void> {
    // Note: With TTL, Redis automatically cleans up old data
    // This method is optional for manual cleanup

    const cutoff = olderThan.getTime();

    // Clean up old events from timeline
    await this.redis.zremrangebyrank(this.timelineKey(), 0, Math.floor(cutoff / 1000));

    // Sessions will expire automatically with TTL
  }
}
